<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="900px"
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-form v-model="valid" ref="itemForm" @submit.prevent="item_submit">
      <v-card class="elevation-0">
        <v-card-title>
          <v-icon small class="mr-2">mdi-package-variant</v-icon>
          {{ newItem ? "Nuevo Producto" : "Editar Producto" }}
          <v-spacer></v-spacer>
          <v-btn icon @click="close()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Información Básica -->
          <div class="form-section">
            <h4 class="form-section-title">
              <v-icon small class="mr-2">mdi-information</v-icon>
              Información Básica
            </h4>
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="localItem.categoria"
                  label="Categoría"
                  :items="opt('CATEGORIA')"
                  item-text="valor"
                  item-value="codigo"
                  :rules="f_required"
                  outlined
                  dense
                  prepend-inner-icon="mdi-shape"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="localItem.id_proveedor"
                  :items="suppliers"
                  label="Proveedor"
                  item-value="code"
                  item-text="PartyName"
                  :rules="f_required"
                  outlined
                  dense
                  prepend-inner-icon="mdi-domain"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="localItem.nombre"
                  :rules="f_required"
                  label="Nombre del Producto"
                  placeholder="Ingrese un nombre descriptivo"
                  outlined
                  dense
                  prepend-inner-icon="mdi-text"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="localItem.concepto"
                  label="Descripción adicional"
                  rows="2"
                  auto-grow
                  outlined
                  dense
                  prepend-inner-icon="mdi-note-text"
                  hide-details="auto"
                />
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Precios y Costos -->
          <div class="form-section">
            <h4 class="form-section-title">
              <v-icon small class="mr-2">mdi-currency-usd</v-icon>
              Precios y Costos
            </h4>

            <!-- Alerta de margen -->
            <v-alert
              v-if="localItem.costo > 0 && localItem.precio > 0"
              :type="showMarginAlert ? 'warning' : 'info'"
              dense
              outlined
              class="mb-4"
            >
              <div class="d-flex align-center justify-space-between">
                <span>
                  <v-icon small class="mr-2">
                    {{ showMarginAlert ? "mdi-alert" : "mdi-information" }}
                  </v-icon>
                  Margen de ganancia: <strong>{{ calculateMargin }}%</strong>
                </span>
                <span v-if="showMarginAlert" class="text-caption">
                  Se recomienda un margen mínimo del 20%
                </span>
              </div>
            </v-alert>

            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="localItem.costo"
                  label="Costo"
                  prefix="$"
                  type="number"
                  min="0"
                  :rules="f_required"
                  outlined
                  dense
                  prepend-inner-icon="mdi-cash-minus"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="localItem.precio"
                  label="Precio de Venta"
                  prefix="$"
                  type="number"
                  min="0"
                  :rules="f_required"
                  outlined
                  dense
                  prepend-inner-icon="mdi-cash-plus"
                  hide-details="auto"
                  :error="showMarginAlert"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="localItem.tax"
                  label="IVA"
                  type="number"
                  min="0"
                  max="100"
                  suffix="%"
                  :rules="f_tax_required"
                  outlined
                  dense
                  prepend-inner-icon="mdi-percent"
                  hide-details="auto"
                />
              </v-col>
            </v-row>

            <!-- Precios adicionales -->
            <v-row class="mt-3">
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="localItem.descuento"
                  label="Descuento"
                  type="number"
                  min="0"
                  max="100"
                  suffix="%"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="localItem.precioB"
                  label="Precio B"
                  prefix="$"
                  type="number"
                  min="0"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="localItem.precioC"
                  label="Precio C"
                  prefix="$"
                  type="number"
                  min="0"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="localItem.precioD"
                  label="Precio D"
                  prefix="$"
                  type="number"
                  min="0"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
            </v-row>

            <!-- Información de precios adicionales -->
            <v-row class="mt-1">
              <v-col cols="12">
                <div class="text-caption grey--text">
                  <v-icon x-small class="mr-1">mdi-information</v-icon>
                  <strong>Precio B:</strong> Clientes frecuentes |
                  <strong>Precio C:</strong> Mayoristas |
                  <strong>Precio D:</strong> Promocional
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Características del Producto -->
          <div class="form-section">
            <h4 class="form-section-title">
              <v-icon small class="mr-2">mdi-tag-multiple</v-icon>
              Características del Producto
            </h4>
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="localItem.marca"
                  label="Marca"
                  :items="opt('MARCA')"
                  item-text="valor"
                  item-value="codigo"
                  outlined
                  dense
                  prepend-inner-icon="mdi-tag"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="localItem.refe"
                  label="Referencia"
                  outlined
                  dense
                  prepend-inner-icon="mdi-identifier"
                  hide-details="auto"
                  @input="returnCap"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="localItem.color"
                  label="Color"
                  :items="opt('COLOR')"
                  item-text="valor"
                  item-value="codigo"
                  outlined
                  dense
                  prepend-inner-icon="mdi-palette"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="localItem.talla"
                  label="Talla/Tamaño"
                  outlined
                  dense
                  prepend-inner-icon="mdi-ruler"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="localItem.uom"
                  label="Unidad de Medida"
                  outlined
                  dense
                  prepend-inner-icon="mdi-weight"
                  hide-details="auto"
                />
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Foto del producto -->
          <div class="form-section">
            <h4 class="form-section-title">
              <v-icon small class="mr-2">mdi-camera</v-icon>
              Foto del producto
            </h4>
            <v-row align="center">
              <v-col cols="12" sm="4">
                <v-img
                  :src="photoSrc"
                  contain
                  max-height="180"
                  class="grey lighten-4"
                >
                  <template v-slot:placeholder>
                    <div class="d-flex align-center justify-center" style="height: 180px;">
                      <v-icon size="64" color="grey lighten-1">mdi-image-off</v-icon>
                    </div>
                  </template>
                </v-img>
              </v-col>
              <v-col cols="12" sm="8">
                <input
                  ref="photoInput"
                  type="file"
                  accept="image/*"
                  style="display: none;"
                  @change="onPhotoSelected"
                />
                <v-btn
                  color="primary"
                  outlined
                  :loading="uploadingPhoto"
                  :disabled="!canUploadPhoto"
                  @click="$refs.photoInput.click()"
                >
                  <v-icon left>mdi-cloud-upload</v-icon>
                  {{ photoPreview ? "Cambiar foto" : "Subir foto" }}
                </v-btn>
                <p v-if="!canUploadPhoto" class="text-caption grey--text mt-2 mb-0">
                  Completa marca y referencia (arriba) para asociar la foto.
                </p>
                <p v-else class="text-caption grey--text mt-2 mb-0">
                  Se comprime a 800px de ancho. Formato JPEG.
                </p>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Códigos y Control -->
          <div class="form-section">
            <h4 class="form-section-title">
              <v-icon small class="mr-2">mdi-barcode</v-icon>
              Códigos de Identificación
            </h4>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="localItem.ean"
                  label="EAN/Código de Barras"
                  outlined
                  dense
                  prepend-inner-icon="mdi-barcode-scan"
                  hide-details="auto"
                  placeholder="Opcional"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="localItem.sku"
                  label="SKU"
                  readonly
                  outlined
                  dense
                  prepend-inner-icon="mdi-key"
                  hide-details="auto"
                  class="readonly-field"
                  :placeholder="skuPlaceholder"
                >
                  <template v-slot:append>
                    <v-icon small :color="localItem.sku ? 'success' : 'grey'">
                      {{ localItem.sku ? "mdi-check-circle" : "mdi-clock-outline" }}
                    </v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="localItem.plu"
                  label="PLU"
                  readonly
                  outlined
                  dense
                  prepend-inner-icon="mdi-numeric"
                  hide-details="auto"
                  class="readonly-field"
                  :placeholder="pluPlaceholder"
                >
                  <template v-slot:append>
                    <v-icon small :color="localItem.plu ? 'success' : 'grey'">
                      {{ localItem.plu ? "mdi-check-circle" : "mdi-clock-outline" }}
                    </v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-alert type="info" dense text class="mt-2">
                  <v-icon small class="mr-2">mdi-information</v-icon>
                  Los códigos SKU y PLU se generan automáticamente al guardar.
                  {{
                    localItem.ean
                      ? "Su código EAN se mantendrá."
                      : "EAN se generará igual al SKU."
                  }}
                </v-alert>
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            v-if="showLabels && !newItem"
            text
            color="primary"
            @click="$emit('print-labels', localItem)"
          >
            <v-icon left>mdi-printer</v-icon>
            Etiquetas
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="close"> Cancelar </v-btn>
          <v-btn
            color="primary"
            type="submit"
            :loading="loading_status"
            :disabled="!valid"
          >
            <v-icon left>mdi-content-save</v-icon>
            {{ newItem ? "Crear Producto" : "Actualizar Producto" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <!-- Diálogo de confirmación -->
    <v-dialog v-model="dialog_confirm" max-width="400" persistent>
      <v-card class="elevation-0">
        <v-card-title class="text-h6">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Confirmar Acción
        </v-card-title>
        <v-card-text class="pt-4">
          ¿Está seguro que desea desactivar este producto? El producto no se eliminará
          pero quedará marcado como inactivo.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="dialog_confirm = false"> Cancelar </v-btn>
          <v-btn color="warning" @click="delete_item">
            <v-icon left>mdi-check</v-icon>
            Desactivar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para mensajes -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { webserver } from "@/services/webserver.js";
import createItem from "@/utils/createProduct.js";
import { compressAndUploadProductPhoto } from "@/utils/uploadProductPhoto.js";
import { resolveCodes } from "@/utils/skuPluGenerator.js";

export default {
  name: "ProductForm",
  data() {
    return {
      localItem: {},
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },
      edit: false,
      loading_status: false,
      dialog_confirm: false,
      valid: false,
      settings: [],
      photoPreview: null,
      uploadingPhoto: false,
      f_required: [(v) => !!v || "Campo requerido"],
      f_tax_required: [
        (v) => (v !== null && v !== undefined && v !== "") || "El campo IVA es requerido",
      ],
      f_email: [(v) => !v || /.+@.+\..+/.test(v) || "Formato de correo inválido"],
    };
  },
  props: {
    table: {
      type: String,
      required: true,
    },
    item: {
      type: Object,
      required: true,
      default: () => createItem(),
    },
    suppliers: {
      type: Array,
      required: false,
    },
    dialog: {
      type: Boolean,
      required: true,
      default: false,
    },
    newItem: {
      type: Boolean,
      required: true,
      default: false,
    },
    showLabels: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.settings = window.settings || {};
    this.resetForm();
  },
  computed: {
    calculateMargin() {
      if (this.localItem.costo > 0 && this.localItem.precio > 0) {
        const margin =
          ((this.localItem.precio - this.localItem.costo) / this.localItem.precio) * 100;
        return margin.toFixed(2);
      }
      return 0;
    },

    showMarginAlert() {
      return (
        this.calculateMargin < 20 && this.localItem.costo > 0 && this.localItem.precio > 0
      );
    },

    previewCodes() {
      if (!this.localItem.categoria && !this.localItem.descripcion) return null;
      return resolveCodes({
        categoria: this.localItem.categoria,
        referencia: this.localItem.refe,
        descripcion: this.localItem.descripcion,
        ean: this.localItem.ean,
      });
    },

    skuPlaceholder() {
      return this.previewCodes
        ? `Se generará: ${this.previewCodes.sku}`
        : "Requiere categoría y nombre";
    },

    pluPlaceholder() {
      return this.previewCodes
        ? `Se generará: ${this.previewCodes.plu}`
        : "Requiere categoría";
    },

    canUploadPhoto() {
      return !!(this.localItem.marca && this.localItem.refe);
    },

    photoSrc() {
      if (this.photoPreview) return this.photoPreview;
      if (this.localItem.marca && this.localItem.refe) {
        const account =
          (window.profile && window.profile.account) ||
          (window.store && window.store.store_id) ||
          "";
        return `https://moda-stc.com/photos/${account}/${this.localItem.marca}/${this.localItem.refe.toLowerCase()}.jpg`;
      }
      return null;
    },
  },
  methods: {
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    async onPhotoSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (!this.canUploadPhoto) {
        this.showMessage("Define marca y referencia antes de subir la foto", "warning");
        e.target.value = "";
        return;
      }
      this.uploadingPhoto = true;
      try {
        const result = await compressAndUploadProductPhoto({
          file,
          item: this.localItem,
          store: window.store && window.store.store_id,
        });
        this.photoPreview = result.photoDataUrl;
        this.localItem.picture = result.picture;
        this.showMessage("Foto subida exitosamente", "success");
      } catch (err) {
        this.showMessage("Error al subir la foto: " + (err.message || err), "error");
      } finally {
        this.uploadingPhoto = false;
        if (this.$refs.photoInput) this.$refs.photoInput.value = "";
      }
    },

    resetForm() {
      this.localItem = JSON.parse(JSON.stringify(this.item));

      if (this.newItem) {
        this.initializeNewProduct();
      }

      this.$nextTick(() => {
        if (this.$refs.itemForm) {
          this.$refs.itemForm.resetValidation();
        }
      });
    },

    initializeNewProduct() {
      this.localItem.descuento = 0;
      this.localItem.cantidad = 1;
      this.localItem.precioB = 0;
      this.localItem.precioC = 0;
      this.localItem.precioD = 0;
      this.localItem.audit = 0;
      this.localItem.status = 1;
      this.localItem.id_pos = window.profile.store_id;

      this.localItem.sku = "";
      this.localItem.plu = "";
      // NO tocar EAN
    },

    opt(e) {
      if (this.settings[e]) {
        return this.settings[e];
      } else {
        return [{ codigo: "", valor: "" }];
      }
    },

    returnCap(event) {
      if (typeof event === "string") {
        this.localItem.refe = event.toUpperCase();
      } else if (event && event.target) {
        this.localItem[event.target.id] = event.target.value.toUpperCase();
      }
    },

    item_submit() {
      if (!this.$refs.itemForm.validate()) {
        this.showMessage("Complete todos los campos requeridos", "warning");
        return;
      }

      this.loading_status = true;

      // SKU/PLU/EAN deterministas via util compartido — SOLO al crear.
      // Al editar, sku/plu son la identidad del producto (PK id_pos+sku):
      // regenerarlos cambiaría la PK y crearía un producto nuevo en vez de
      // actualizar el existente, así que se preservan los cargados.
      // Misma (categoria + referencia + descripcion) genera mismos códigos
      // que /aiDocImport y /aiMasterImport. EAN tipeado gana sobre el calculado.
      if (this.newItem) {
        const codes = resolveCodes({
          categoria: this.localItem.categoria,
          referencia: this.localItem.refe,
          descripcion: this.localItem.descripcion,
          ean: this.localItem.ean,
        });
        this.localItem.sku = codes.sku;
        this.localItem.plu = codes.plu;
        if (!this.localItem.ean || String(this.localItem.ean).trim() === "") {
          this.localItem.ean = codes.ean;
        }
      }

      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];

      if (this.newItem) {
        this.localItem.first_in = dateStr;
      }
      this.localItem.last_in = dateStr;

      this.localItem.id_pos = window.profile.store_id.substring(0, 6);

      if (this.localItem.status === undefined || this.localItem.status === null) {
        this.localItem.status = 1;
      }

      this.localItem.cantidad = this.localItem.cantidad || 1;
      this.localItem.descuento = this.localItem.descuento || 0;
      this.localItem.precioB = this.localItem.precioB || 0;
      this.localItem.precioC = this.localItem.precioC || 0;
      this.localItem.precioD = this.localItem.precioD || 0;
      this.localItem.audit = this.localItem.audit || 0;

      this.localItem.cantidad = Number(this.localItem.cantidad);
      this.localItem.descuento = Number(this.localItem.descuento);
      this.localItem.precioB = Number(this.localItem.precioB);
      this.localItem.precioC = Number(this.localItem.precioC);
      this.localItem.precioD = Number(this.localItem.precioD);
      this.localItem.costo = Number(this.localItem.costo);
      this.localItem.precio = Number(this.localItem.precio);
      this.localItem.tax = Number(this.localItem.tax);
      this.localItem.audit = Number(this.localItem.audit);

      const stringFields = ["grupo", "marca", "refe", "color", "talla"];
      stringFields.forEach((field) => {
        if (!this.localItem[field] || this.localItem[field].trim() === "") {
          this.localItem[field] = "";
        }
      });

      const qry = {
        store: window.profile.store_id,
        table: "items",
        data: [this.localItem],
      };

      console.log("Enviando datos al servidor:", qry);
      webserver(
        "put_table_admin",
        qry,
        () => {
          this.loading_status = false;
          this.showMessage("Producto guardado", "success");
          this.$emit("refresh", true);
          setTimeout(() => this.close(), 500);
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error: " + error, "error");
        }
      );
    },

    delete_item() {
      this.loading_status = true;
      this.localItem.status = 0;

      const qry = {
        store: window.profile.store_id,
        table: this.table,
        data: [this.localItem],
      };

      webserver(
        "put_table",
        qry,
        () => {
          this.loading_status = false;
          this.dialog_confirm = false;
          this.showMessage("Producto desactivado exitosamente", "success");
          this.$emit("refresh", true);
          setTimeout(() => this.close(), 1000);
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al desactivar el producto: " + error, "error");
        }
      );
    },

    close() {
      this.resetForm();
      this.$emit("close", false);
    },
  },
  watch: {
    item: {
      handler(newVal) {
        this.localItem = JSON.parse(JSON.stringify(newVal));
        if (this.newItem) {
          this.initializeNewProduct();
        }
      },
      deep: true,
    },

    dialog(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
  },
};
</script>

<style scoped>
.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.dialog-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

.form-section {
  margin-bottom: 0;
}

.form-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #666;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.readonly-field >>> .v-input__control {
  background-color: #f5f5f5;
}

.readonly-field >>> input {
  color: #666 !important;
}

.v-alert {
  margin-bottom: 0;
}

.v-text-field {
  margin-bottom: 0;
}

@media (max-width: 600px) {
  .form-section-title {
    font-size: 0.8rem;
  }

  .dialog-title {
    font-size: 1rem;
  }
}
</style>
